import { useMemo } from 'react';
import { FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SelectField } from 'common/forms/Field';
import { ITemplate } from 'graphql/types';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

interface IProps {
    name: string;
}

export const CertificateTemplateSelectField = <
    TFieldValues extends FieldValues,
>({
    control: formControl,
    name,
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();

    const control = asTypedControl<ITemplate>(name, formControl);

    const {
        formState: { defaultValues },
    } = useController({ name, control });

    const { templateOptions, defaultValue } = useMemo(() => {
        const templateOptions = [
            {
                label: translate(
                    'certificateTemplateSelectField.academyModern'
                ),
                value: ITemplate.AcademyModern,
            },
            {
                label: translate('certificateTemplateSelectField.academySplit'),
                value: ITemplate.AcademySplit,
            },
            {
                label: translate('certificateTemplateSelectField.custom'),
                value: ITemplate.Custom,
            },
        ];

        const oldTemplateOptions = [
            {
                label: translate('certificateTemplateSelectField.modern'),
                value: ITemplate.Modern,
                disabled: true,
            },
            {
                label: translate('certificateTemplateSelectField.fresh'),
                value: ITemplate.Fresh,
                disabled: true,
            },
            {
                label: translate('certificateTemplateSelectField.classic'),
                value: ITemplate.Classic,
                disabled: true,
            },
            {
                label: translate('certificateTemplateSelectField.split'),
                value: ITemplate.Split,
                disabled: true,
            },
        ];

        const defaultValue =
            defaultValues?.template || templateOptions[0].value;

        let finalTemplateOptions = [...templateOptions];

        // If the default value is an old template, we want to add it to the new template option
        // for displaying as disabled option
        if (
            !finalTemplateOptions.some(
                (option) => option.value === defaultValue
            )
        ) {
            const oldTemplateOption = oldTemplateOptions.find(
                (option) => option.value === defaultValue
            );

            if (oldTemplateOption) {
                finalTemplateOptions = [
                    ...finalTemplateOptions,
                    oldTemplateOption,
                ];
            }
        }

        return { templateOptions: finalTemplateOptions, defaultValue };
    }, [translate, defaultValues?.template]);

    return (
        <SelectField
            control={control}
            defaultValue={defaultValue}
            name={name}
            options={templateOptions}
        />
    );
};
